import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { IMaskInput } from 'react-imask';
import type { AppInputProps } from './AppInput.types';
import {
  generateInputId,
  isFieldFilled,
  formatErrorText,
  shouldShowError,
  combineClassNames,
} from './utils/inputHelpers';
import './AppInput.css';

// Расширяем тип ref для поддержки метода updateMaskKey
export interface AppInputRef extends HTMLInputElement {
  updateMaskKey: () => void;
}

const AppInput = forwardRef<AppInputRef, AppInputProps>(({
  // Основные пропсы
  value,
  defaultValue,
  placeholder,
  disabled = false,
  readOnly = false,
  
  // Label и описание
  label,
  required = false,
  
  // Ошибки
  error,
  errorText,
  
  // Визуальные настройки
  size = 'L',
  background = 'default',
  roundedBottom = true,
  
  // Иконки
  iconLeft,
  iconRight,
  
  // Суффикс
  suffix,
  
  // Обработчики
  onChange,
  onBlur,
  onFocus,
  
  // Управление фокусом
  focused,
  
  // HTML атрибуты
  name,
  id,
  autoComplete,
  autoFocus = false,
  className,
  
  // Для расширения специализированными компонентами
  inputProps,
  
  // Пропсы для маски
  mask,
  unmask,
  placeholderChar = '_',
  lazy = false,
  onAccept,
  onComplete,
  
  'data-testid': dataTestId,
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  // Состояние для key - обновляется только при программном изменении (blur)
  const [maskKey, setMaskKey] = useState(0);
  // Запоминаем последнее значение для сравнения
  const lastValueRef = useRef(value);
  // Флаг для отслеживания программных обновлений (из props)
  const isProgrammaticUpdateRef = useRef(false);
  // Флаг: ближайшее изменение value-пропа вызвано вводом пользователя в этом же
  // компоненте (через onAccept), а не внешним/программным обновлением.
  const isInternalUpdateRef = useRef(false);
  // Ссылка на IMask instance для принудительного обновления значения
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maskInstanceRef = useRef<any>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Автофокус для masked input (IMaskInput не поддерживает autoFocus нативно)
  useEffect(() => {
    if (autoFocus && mask && inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Управление фокусом через проп focused
  useEffect(() => {
    // Если проп focused не передан, управление фокусом внутреннее
    if (focused === undefined) return;
    
    const input = inputRef.current;
    if (!input) return;
    
    if (focused && document.activeElement !== input) {
      input.focus();
    } else if (!focused && document.activeElement === input) {
      input.blur();
    }
  }, [focused]);
  
  // Forwarding ref to internal input ref + добавляем метод updateMaskKey
  useImperativeHandle(ref, () => ({
    ...inputRef.current!,
    updateMaskKey: () => {
      isProgrammaticUpdateRef.current = true;
      lastValueRef.current = value;
      
      // Если есть maskInstance, устанавливаем unmaskedValue
      if (maskInstanceRef.current && value !== undefined) {
        const valueStr = String(value);
        // Для unmask="typed" нужно установить именно те символы, которые пользователь ввел бы
        // Убираем все НЕ-цифровые символы, кроме +
        let unmaskedValue = valueStr.replace(/[^\d+]/g, '');
        
        // Если unmask="typed", убираем +7 в начале (это фиксированная часть маски)
        if (unmask === 'typed' && unmaskedValue.startsWith('+7')) {
          unmaskedValue = unmaskedValue.substring(2);
        } else if (unmaskedValue.startsWith('+')) {
          unmaskedValue = unmaskedValue.substring(1);
        }
        
        maskInstanceRef.current.unmaskedValue = unmaskedValue;
      } else {
        // Иначе перерисовываем компонент через изменение key
        setMaskKey(prev => prev + 1);
      }
    },
  }));
  
  // Отслеживаем изменения value из props для маски
  useEffect(() => {
    if (!mask || value === undefined) return;

    // Эхо того же ввода: IMask уже записал lastValueRef в onAccept, эффект
    // не должен пересоздавать маску — но флаг всё равно сбрасываем, иначе
    // следующее внешнее изменение (например «11» → «11:00» на blur) тоже
    // посчитается эхом и поле визуально останется неполным.
    if (value === lastValueRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    lastValueRef.current = value;

    if (isInternalUpdateRef.current) {
      // Эхо ввода, которое обёртка успела трансформировать (AppPhoneInput и т.п.)
      isInternalUpdateRef.current = false;
      return;
    }

    isProgrammaticUpdateRef.current = true;
    setMaskKey((prev) => prev + 1);
  }, [value, mask]);
  
  // Определяем, является ли компонент controlled или uncontrolled
  const isControlled = value !== undefined;
  const inputValue = isControlled ? (value as string) : internalValue;
  
  // Prepare masked input value
  const maskedInputValue = useMemo<string | undefined>(() => {
    if (!inputValue) return undefined;
    // Explicit type assertion since we know inputValue is a string from our props
    return String(inputValue) as string;
  }, [inputValue]);
  
  // Генерируем ID если не предоставлен
  const inputId = id || generateInputId('app-input');
  const errorId = `${inputId}-error`;
  
  // Определяем состояния
  const hasError = shouldShowError(error);
  const isFilled = isFieldFilled(inputValue);
  const errorMessage = error ? formatErrorText(error, errorText) : '';
  const { title: inputTitle, ...restInputProps } = inputProps ?? {};
  const overflowTitle = inputTitle ?? (isFilled ? String(inputValue) : undefined);
  
  // Обработчики событий
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };
  
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    isInternalUpdateRef.current = false;

    // Если значение изменилось извне (программно), обновляем maskKey
    if (mask && value !== undefined && value !== lastValueRef.current) {
      lastValueRef.current = value;
      setMaskKey((prev) => prev + 1);
    }
    
    onBlur?.(event);
  };
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    
    if (!isControlled) {
      setInternalValue(newValue);
    }
    
    onChange?.(newValue, event);
  };

  const wrapperClasses = classNames(
    'app-input',
    {
      'app-input_disabled': disabled,
    },
    combineClassNames(className)
  );

  const containerClasses = classNames(
    'app-input__container',
    `app-input__container_size_${size}`,
    `app-input__container_background_${background}`,
    `app-input__container_rounded-bottom_${roundedBottom}`,
    {
      'app-input__container_error': hasError,
      'app-input__container_focused': isFocused,
      'app-input__container_disabled': disabled,
    }
  );

  const labelClasses = classNames(
    'app-input__label',
    {
      'app-input__label_error': hasError,
    }
  );

  const requiredClasses = classNames(
    'app-input__required',
    {
      'app-input__required_error': hasError,
    }
  );

  const fieldClasses = classNames(
    'app-input__field',
    `app-input__field_size_${size}`
  );

  return (
    <div 
      className={wrapperClasses}
      data-testid={dataTestId}
    >
      {/* Label */}
      {label && (
        <label 
          htmlFor={inputId}
          className={labelClasses}
          data-testid={dataTestId ? `${dataTestId}-label` : undefined}
        >
          {label}
          {required && (
            <span className={requiredClasses}>
              *
            </span>
          )}
        </label>
      )}
      
      {/* Input Container */}
      <div className={containerClasses}>
        {/* Left Icon */}
        {iconLeft && (
          <span className="app-input__icon">
            {iconLeft}
          </span>
        )}
        
        {/* Input - с маской или без */}
        {mask ? (
          <IMaskInput
            key={`mask-${maskKey}`}
            mask={mask}
            unmask={unmask}
            lazy={lazy}
            placeholderChar={placeholderChar}
            // @ts-expect-error - type mismatch between inputValue and IMaskInput value prop
            value={maskedInputValue}
            onAccept={(value, maskRefInstance) => {
              // Сохраняем ссылку на maskInstance для использования в updateMaskKey
              maskInstanceRef.current = maskRefInstance;

              // Запоминаем, что именно это значение уже "наше" (пришло от IMask, а не снаружи),
              // ВСЕГДА — независимо от ветки ниже. Раньше это делалось только в "обычной" ветке,
              // но флаг isProgrammaticUpdateRef сбрасывается асинхронно (setTimeout 0) и на
              // момент следующего реального нажатия клавиши мог быть ещё true — тогда именно
              // пользовательский ввод уходил в "программную" ветку без обновления ref, эффект
              // синхронизации считал это внешним изменением и пересоздавал IMaskInput через key
              // на КАЖДОЕ нажатие клавиши, сбрасывая курсор и фокус.
              lastValueRef.current = value;

              // Если это программное обновление (из props), не вызываем onChange
              if (isProgrammaticUpdateRef.current) {
                // НЕ сбрасываем флаг сразу - отложим на следующий тик
                // чтобы перехватить ВСЕ события от IMask
                setTimeout(() => {
                  isProgrammaticUpdateRef.current = false;
                }, 0);
                onAccept?.(value, maskRefInstance);
                return;
              }

              const newValue = unmask ? maskRefInstance.unmaskedValue : value;
              // Помечаем, что следующее изменение value-пропа — эхо этого же ввода
              // пользователя (даже если обёртка вроде AppPhoneInput трансформирует
              // значение перед тем, как вернуть его обратно как controlled value).
              // Иначе эффект ниже принимает свой же ввод за внешнее программное
              // изменение и пересоздаёт IMaskInput через key — фокус слетает на
              // каждый введённый символ.
              isInternalUpdateRef.current = true;
              if (!isControlled) {
                setInternalValue(newValue);
              }
              onChange?.(newValue, {} as React.ChangeEvent<HTMLInputElement>);
              onAccept?.(value, maskRefInstance);
            }}
            onComplete={(value, maskRefInstance) => {
              onComplete?.(value, maskRefInstance);
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            readOnly={readOnly}
            autoComplete={autoComplete}
            placeholder={placeholder}
            inputRef={inputRef}
            id={inputId}
            name={name}
            className={fieldClasses}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            data-filled={isFilled}
            {...restInputProps}
            title={overflowTitle}
          />
        ) : (
          <input
            ref={inputRef}
            id={inputId}
            name={name}
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            className={fieldClasses}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            data-filled={isFilled}
            {...restInputProps}
            title={overflowTitle}
          />
        )}
        
        {/* Suffix */}
        {suffix && (
          <span className="app-input__suffix">
            {suffix}
          </span>
        )}
        
        {/* Right Icon */}
        {iconRight && (
          <span className="app-input__icon">
            {iconRight}
          </span>
        )}
      </div>
      
      {/* Error Text */}
      {hasError && errorMessage && (
        <span
          className="app-input__error"
          id={errorId}
          title={errorMessage}
          data-testid={dataTestId ? `${dataTestId}-error` : undefined}
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
});

AppInput.displayName = 'AppInput';

export default AppInput;